/**
 * Project-scope migration (card 7.1): one project per API workspace, bound to
 * it, and existing projects that hold collections bound to their workspace.
 * The plan is projectScopeMigrationPlan.js; this file reads, writes and marks.
 *
 * IDEMPOTENT, per bucket, per device: a versioned marker in localStorage
 * (`apiboo.projectScope.migrated.v1:<scopeKey>`) says this device has migrated
 * that bucket. It is per DEVICE on purpose: local and git workspaces exist on
 * one device only, so a marker synced from another device would skip this
 * device's own workspaces. Two devices are still safe together — the plan's
 * project ids are deterministic and it never touches an existing project.
 *
 * Not marked (so the next start retries): no workspaces loaded yet, a bucket
 * that failed to read or write, a workspace whose collections could not be
 * read. Team buckets are only bound, never given new projects, and skipped
 * entirely when the user cannot write them (a viewer's push poisons the
 * outbox — boardRoles.js).
 */
import { canWriteScope, listLinks, listProjects, saveProject } from '../graph/projectsStore';
import { graphRecordScopes } from '../graph/graphScopes';
import { planScopeMigration } from './projectScopeMigrationPlan';
import { bindingOf, holdsWorkspace } from './projectBindingFields';
import { setActiveProject } from './projectScopeStore';

export const MIGRATION_VERSION = 1;
export const migrationMarkerKey = (scopeKey) => `apiboo.projectScope.migrated.v${MIGRATION_VERSION}:${scopeKey}`;

function migrated(scopeKey) {
  try {
    return window.localStorage.getItem(migrationMarkerKey(scopeKey)) != null;
  } catch {
    return false;
  }
}

function mark(scopeKey) {
  try {
    window.localStorage.setItem(migrationMarkerKey(scopeKey), new Date().toISOString());
  } catch {
    // Not marked: the next start plans again and finds nothing left to do.
  }
}

/** Map<collectionId, workspaceId> over every workspace, plus the ones that failed to read. */
async function readCollectionMap(workspaces, loadCollectionIds) {
  const map = new Map();
  const unread = new Set();
  for (const ws of workspaces) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const ids = await loadCollectionIds(ws);
      for (const id of ids || []) if (!map.has(String(id))) map.set(String(id), String(ws.id));
    } catch {
      unread.add(String(ws.id));
    }
  }
  return { map, unread };
}

/**
 * @param workspaces          workspaceStore.workspaces (an empty list does nothing and marks nothing)
 * @param loadCollectionIds   async (workspace) => collection ids in it
 * @param currentWorkspaceId  becomes the active project when the personal bucket migrates for the first time
 * @returns {Promise<{ scopes: Array<{ scopeKey, status, bound, created }> }>}
 */
export async function runProjectScopeMigration({ workspaces = [], loadCollectionIds, currentWorkspaceId = null } = {}) {
  const list = (workspaces || []).filter((w) => w?.id != null);
  if (!list.length) return { scopes: [] };
  const buckets = graphRecordScopes().scopes.filter((s) => s.location !== 'device');
  const pending = buckets.filter((s) => !migrated(s.scopeKey));
  if (!pending.length) return { scopes: buckets.map((s) => ({ scopeKey: s.scopeKey, status: 'done', bound: 0, created: 0 })) };

  const { map, unread } = loadCollectionIds ? await readCollectionMap(list, loadCollectionIds) : { map: new Map(), unread: new Set(list.map((w) => String(w.id))) };
  const out = [];
  for (const s of buckets) {
    if (!pending.includes(s)) {
      out.push({ scopeKey: s.scopeKey, status: 'done', bound: 0, created: 0 });
      continue;
    }
    if (!canWriteScope(s.scopeKey)) {
      out.push({ scopeKey: s.scopeKey, status: 'readOnly', bound: 0, created: 0 });
      continue;
    }
    const personal = s.teamId == null;
    try {
      // eslint-disable-next-line no-await-in-loop
      const [projects, links] = await Promise.all([listProjects(s.scopeKey), listLinks(s.scopeKey)]);
      const plan = planScopeMigration({ teamId: s.teamId, projects, links, workspaces: list, collectionWorkspace: map, unreadWorkspaceIds: unread, createMissing: personal });
      for (const { project, workspace } of plan.bind) {
        // eslint-disable-next-line no-await-in-loop
        await saveProject({ ...project, ...bindingOf(workspace) }, s.scopeKey);
      }
      for (const draft of plan.create) {
        // eslint-disable-next-line no-await-in-loop
        await saveProject(draft, s.scopeKey);
      }
      if (!unread.size) mark(s.scopeKey);
      if (personal && currentWorkspaceId != null) activateWorkspaceProject(currentWorkspaceId, [...projects, ...plan.create], plan.bind);
      out.push({ scopeKey: s.scopeKey, status: unread.size ? 'partial' : 'migrated', bound: plan.bind.length, created: plan.create.length });
    } catch {
      out.push({ scopeKey: s.scopeKey, status: 'failed', bound: 0, created: 0 });
    }
  }
  return { scopes: out };
}

/** The current workspace's project becomes the active scope (first migration only). */
function activateWorkspaceProject(workspaceId, projects, binds) {
  const id = String(workspaceId);
  const viaBind = binds.find((b) => String(b.workspace.id) === id)?.project.id;
  const viaRecord = projects.find((p) => holdsWorkspace(p, id))?.id;
  const target = viaBind || viaRecord;
  if (target) setActiveProject(target);
}
